import React from 'react';
import { generatePath, Navigate, Outlet, useLocation } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { CMS_KEYS } from 'api/cms/constants';
import { IUserManual } from 'api/cms/types';
import { VIEW_MERCHANT_PAGE } from 'api/merchants/constants';
import { RECONCILIATION_KEYS } from 'api/reconciliation/constant';
import { IReconciliationFolder } from 'api/reconciliation/types';
import { USERS_KEYS } from 'api/users/constants';
import { IUser } from 'api/users/types';
import { ICONS_MAP } from 'constants/icons';
import { PERMISSION_TYPE } from 'hooks/useRBAC';
import { queryClient } from 'queries';
import { normalizeQueryKey } from 'queries/utils';
import { CLIENTS_CHILDREN } from 'routes/config/routesChildren/clients';
import { INTEGRATIONS_CHILDREN } from 'routes/config/routesChildren/integrations';
import { MERCHANT_SETTINGS_CHILDREN } from 'routes/config/routesChildren/merchantSettings';
import { PSP_MANAGEMENT_CHILDREN } from 'routes/config/routesChildren/pspManagement';
import { SETTINGS_CHILDREN } from 'routes/config/routesChildren/settings';
import { TRAFFIC_MANAGEMENT_CHILDREN } from 'routes/config/routesChildren/trafficManagement';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';
import ClientsLayout from 'routes/pages/AuthorizedStack/Clients/Layout';

import { SETTLEMENT_CHILDREN } from './routesChildren/settlement';

import { ROUTES } from './constants';

const Dashboard = appLazy(
  () => import('routes/pages/AuthorizedStack/Dashboards/FolderDashboards/Dashboard/View'),
);
const CreateDashboard = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/Dashboards/FolderDashboards/Dashboard/Create'),
);
const EditDashboard = appLazy(
  () => import('routes/pages/AuthorizedStack/Dashboards/FolderDashboards/Dashboard/Edit'),
);
const NotificationHistory = appLazy(
  () => import('routes/pages/AuthorizedStack/NotificationHistory'),
);

const CreateGraph = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Dashboards/FolderDashboards/Dashboard/View/Graph/Create'
    ),
);
const EditGraph = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Dashboards/FolderDashboards/Dashboard/View/Graph/Edit'
    ),
);

const CreateBillingContract = appLazy(
  () => import('routes/pages/AuthorizedStack/Invoices/BillingContracts/Create'),
);
const EditBillingContract = appLazy(
  () => import('routes/pages/AuthorizedStack/Invoices/BillingContracts/Edit'),
);
const Invoices = appLazy(() => import('routes/pages/AuthorizedStack/Invoices'));

const EditGatewayInvoice = appLazy(
  () => import('routes/pages/AuthorizedStack/Invoices/GatewayInvoices/EditForm'),
);

const HomeLayout = appLazy(() => import('routes/pages/HomeLayout'));
const Layout = appLazy(() => import('routes/pages/AuthorizedStack/Layout'));
const ShortLink = appLazy(() => import('routes/pages/AuthorizedStack/ShortLink'));

const SignIn = appLazy(
  () => import('routes/pages/UnauthorizedStack/Authentication/SignIn'),
);
const ForgotPassword = appLazy(
  () => import('routes/pages/UnauthorizedStack/Authentication/ForgotPassword'),
);
const SetPassword = appLazy(
  () => import('routes/pages/UnauthorizedStack/Authentication/SetPassword'),
);

const SettlementLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/Settlement/Layout'),
);
const PspManagementLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/PspManagement/Layout'),
);
const MerchantsSettingsLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/Layout'),
);
const TrafficManagementLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/TrafficManagement/Layout'),
);
const SettingsLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/Layout'),
);
const IntegrationsLayout = appLazy(
  () => import('routes/pages/AuthorizedStack/Integrations/Layout'),
);

const Transactions = appLazy(() => import('routes/pages/AuthorizedStack/Transactions'));
const Transaction = appLazy(
  () => import('routes/pages/AuthorizedStack/Transactions/Transaction'),
);
const RoutingHistory = appLazy(
  () => import('routes/pages/AuthorizedStack/Transactions/Transaction/RoutingHistory'),
);

const Dashboards = appLazy(() => import('routes/pages/AuthorizedStack/Dashboards'));
const CopyDashboardFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Dashboards/Folders/Folder/Copy'),
);
const CreateDashboardFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Dashboards/Folders/Folder/Create'),
);
const EditDashboardFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Dashboards/Folders/Folder/Edit'),
);

const UserManagement = appLazy(
  () => import('routes/pages/AuthorizedStack/UserManagement'),
);
const User = appLazy(
  () => import('routes/pages/AuthorizedStack/UserManagement/Users/User/View'),
);
const UserCreate = appLazy(
  () => import('routes/pages/AuthorizedStack/UserManagement/Users/User/Create'),
);
const UserEdit = appLazy(
  () => import('routes/pages/AuthorizedStack/UserManagement/Users/User/Edit'),
);
const CreateRole = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/UserManagement/RolesAndPermissions/Role/Create'),
);
const EditRole = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/UserManagement/RolesAndPermissions/Role/Edit'),
);

const Merchants = appLazy(() => import('routes/pages/AuthorizedStack/Merchants'));
const CreateMerchant = appLazy(
  () => import('routes/pages/AuthorizedStack/Merchants/Merchant/Create'),
);
const Organizations = appLazy(() => import('routes/pages/AuthorizedStack/Organizations'));
const ViewOrganization = appLazy(
  () => import('routes/pages/AuthorizedStack/Organizations/Organization/View'),
);
const Merchant = appLazy(
  () => import('routes/pages/AuthorizedStack/Merchants/Merchant/View'),
);
const EditMerchant = appLazy(
  () => import('routes/pages/AuthorizedStack/Merchants/Merchant/Edit'),
);
const Stylization = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/Stylization'),
);
const ThemeCreate = appLazy(
  () => import('routes/pages/AuthorizedStack/ThemeEditor/Create'),
);
const ThemeEdit = appLazy(() => import('routes/pages/AuthorizedStack/ThemeEditor/Edit'));
const HFThemeCreate = appLazy(
  () => import('routes/pages/AuthorizedStack/ThemeEditor/HFCreate'),
);
const HFThemeEdit = appLazy(
  () => import('routes/pages/AuthorizedStack/ThemeEditor/HFEdit'),
);

const NotFound = appLazy(
  () => import('routes/pages/UnauthorizedStack/SomethingWrongPages/NotFound'),
);
const PermissionDenied = appLazy(
  () => import('routes/pages/UnauthorizedStack/SomethingWrongPages/PermissionDenied'),
);
const SomethingWrong = appLazy(
  () => import('routes/pages/UnauthorizedStack/SomethingWrongPages/SomethingWrong'),
);
const CreateLimit = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/MerchantsSettings/CashierLimits/Limit/Create'),
);
const CreateRoutingRuleset = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Create'
    ),
);
const CreateCashierSettings = appLazy(
  () => import('routes/pages/AuthorizedStack/MerchantsSettings/CashierSettings/Create'),
);
const CreateChangeRule = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/ChangePaymentMethod/Rule/Create'
    ),
);
const EditChangeRule = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/ChangePaymentMethod/Rule/Edit'
    ),
);
const CreateCashierPaymentMethod = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/MerchantsSettings/PaymentMethods/PaymentMethod/Create'
    ),
);
const CreateCashierPaymentMethodGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/CashierPaymentMethodGroups/Create'
    ),
);
const EditCashierPaymentMethodGroup = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/CashierPaymentMethodGroups/Edit'
    ),
);
const CreateLegalEntity = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Organizations/Organization/View/LegalEntities/Create'
    ),
);
const EditLegalEntity = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Organizations/Organization/View/LegalEntities/Edit'
    ),
);
const AuditLogs = appLazy(() => import('routes/pages/AuthorizedStack/AuditLogs'));
const CreatePaymentLink = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/PaymentLinks/PaymentLink/Create'
    ),
);
const EditPaymentLink = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Merchants/Merchant/View/PaymentLinks/PaymentLink/Edit'
    ),
);
const UserManual = appLazy(() => import('routes/pages/AuthorizedStack/UserManual'));
const ManualSection = appLazy(
  () => import('routes/pages/AuthorizedStack/UserManual/ViewSection'),
);
const MessageCenter = appLazy(() => import('routes/pages/AuthorizedStack/MessageCenter'));

const Reconciliation = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation'),
);
const ViewReconciliationFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation/Folder/View'),
);
const CreateReconciliationFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation/Folder/Create'),
);
const EditReconciliationFolder = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation/Folder/Edit'),
);
const ViewReconciliationFile = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation/Folder/View/FileView'),
);
const ViewReconciliationMapping = appLazy(
  () => import('routes/pages/AuthorizedStack/Reconciliation/Folder/View/MappingSchema'),
);
export interface IRouteParams {
  code?: string;
  fileId?: string;
  id?: string;
  linkId?: string;
  targetId?: string;
  targetType?: string;
  themeId?: string;
}

export interface IRoutesConfigItem {
  breadcrumb?: string | TDynamicBreadCrumbFunction;
  children?: IRoutesConfigItem[];
  component: typeof Outlet | typeof Transactions;
  disableLazy?: boolean;
  intlKey?: string;
  isParentFolder?: boolean;
  isPrivate?: boolean;
  isSubFolder?: boolean;
  navBarIcon?: React.ReactElement;
  path: string;
  permissions?: PERMISSIONS[];
  permissionType?: PERMISSION_TYPE;
  testId?: string;
  url?: string | TDynamicUrlFunction;
}
type TDynamicBreadCrumbFunction = (params?: IRouteParams) => string;

type TDynamicUrlFunction = (params?: IRouteParams) => string;

const LEGACY_INTEGRATION_BUILDER_PATH = '/integration-builder';
const LEGACY_RESULT_CODES_PATH = '/settings/result-codes';

const LegacyIntegrationBuilderRedirect = () => {
  const { pathname, search } = useLocation();

  return (
    <Navigate
      replace
      to={{
        pathname: pathname.replace(
          LEGACY_INTEGRATION_BUILDER_PATH,
          ROUTES.INTEGRATIONS.SUB_PATH.INTEGRATION_BUILDER.PATH,
        ),
        search,
      }}
    />
  );
};

const LegacyResultCodesRedirect = () => {
  const { search } = useLocation();

  return (
    <Navigate
      replace
      to={{ pathname: ROUTES.INTEGRATIONS.SUB_PATH.RESULT_CODES.PATH, search }}
    />
  );
};

export const ROUTES_CONFIG = [
  {
    component: SignIn,
    path: ROUTES.SIGN_IN.PATH,
  },
  {
    component: ForgotPassword,
    path: ROUTES.FORGOT_PASSWORD.PATH,
  },
  {
    component: ForgotPassword,
    path: ROUTES.RESET_PASSWORD.PATH,
  },
  {
    component: SetPassword,
    path: ROUTES.SET_PASSWORD.PATH,
  },
  {
    children: [
      {
        children: [
          {
            component: HomeLayout,
            path: ROUTES.HOME.PATH,
          },
          {
            component: ShortLink,
            path: ROUTES.SHORT_LINK.PATH,
          },
          {
            component: NotificationHistory,
            path: ROUTES.NOTIFICATION_HISTORY.PATH,
            url: ROUTES.NOTIFICATION_HISTORY.PATH,
          },
          {
            breadcrumb: 'transactions.title',
            children: [
              {
                breadcrumb: 'transactions.title',
                component: Transactions,
                navBarIcon: <ICONS_MAP.TransactionMenu />,
                path: ROUTES.TRANSACTIONS.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_TRANSACTIONS],
                testId: 'navbarTransactions',
              },
              {
                component: Outlet,
                path: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.PATH,
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.TRANSACTIONS.SUB_PATH.VIEW;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },

                breadcrumb: (params: IRouteParams): string => {
                  return params?.id || 'transaction.title';
                },
                children: [
                  {
                    component: Transaction,
                    path: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_TRANSACTIONS],
                  },
                  {
                    breadcrumb: 'routingHistory.title',
                    component: RoutingHistory,
                    path: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.SUB_PATH.ROUTING_HISTORY.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_TRANSACTION_ROUTING_HISTORY],
                    url: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.SUB_PATH.ROUTING_HISTORY.PATH,
                  },
                  {
                    breadcrumb: 'auditLogs.title',
                    component: AuditLogs,
                    path: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS.PATH,
                    permissions: [
                      PERMISSIONS.CAN_VIEW_TRANSACTIONS,
                      PERMISSIONS.VIEW_AUDIT_LOGS,
                    ],
                    url: ROUTES.TRANSACTIONS.SUB_PATH.VIEW.SUB_PATH.AUDIT_LOGS.PATH,
                  },
                ],
              },
            ],
            component: Outlet,
            path: ROUTES.TRANSACTIONS.PATH,
            url: ROUTES.TRANSACTIONS.PATH,
          },
          {
            breadcrumb: 'reconciliation.title',
            children: [
              {
                breadcrumb: 'reconciliation.title',
                component: Reconciliation,
                navBarIcon: <ICONS_MAP.Reconciliation />,
                path: ROUTES.RECONCILIATION.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_RECONCILIATION_FOLDER],
                testId: 'navbarReconciliation',
              },
              {
                component: CreateReconciliationFolder,
                path: ROUTES.RECONCILIATION.SUB_PATH.CREATE_RECONCILIATION_FOLDER,
                permissions: [PERMISSIONS.CAN_CREATE_RECONCILIATION_FOLDER],
              },
              {
                component: EditReconciliationFolder,
                path: ROUTES.RECONCILIATION.SUB_PATH.EDIT_RECONCILIATION_FOLDER.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_RECONCILIATION_FOLDER],
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } =
                    ROUTES.RECONCILIATION.SUB_PATH.EDIT_RECONCILIATION_FOLDER;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
              {
                breadcrumb: (params?: IRouteParams): string => {
                  if (!params?.id) return '';
                  const folder = queryClient.getQueryData<IReconciliationFolder>(
                    normalizeQueryKey([
                      RECONCILIATION_KEYS.RECONCILIATION_FOLDER,
                      params.id,
                    ]),
                  );
                  return folder ? folder.name || '' : 'reconciliation.folder.title';
                },
                children: [
                  {
                    component: ViewReconciliationFolder,
                    path: ROUTES.RECONCILIATION.SUB_PATH.VIEW.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_RECONCILIATION_FOLDER],
                  },
                  {
                    breadcrumb: (params?: IRouteParams): string => {
                      if (!params?.fileId) return '';
                      return params?.fileId;
                    },
                    component: ViewReconciliationFile,
                    path: ROUTES.RECONCILIATION.SUB_PATH.VIEW.SUB_PATH.FILE.PATH,
                    url: (params) => {
                      const id = params?.id;
                      const fileId = params?.fileId;
                      const { PARAMS, PATH } =
                        ROUTES.RECONCILIATION.SUB_PATH.VIEW.SUB_PATH.FILE;
                      return !id || !fileId
                        ? ''
                        : generatePath(PATH, {
                            [PARAMS.FILE_ID]: fileId,
                            [PARAMS.ID]: id,
                          });
                    },
                  },
                  {
                    breadcrumb: (): string => {
                      return 'reconciliation.folder.mapping.title';
                    },
                    component: ViewReconciliationMapping,
                    path: ROUTES.RECONCILIATION.SUB_PATH.VIEW.SUB_PATH.MAPPING.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_RECONCILIATION_MAPPING],
                    url: (params) => {
                      const id = params?.id;
                      const { PARAMS, PATH } =
                        ROUTES.RECONCILIATION.SUB_PATH.VIEW.SUB_PATH.MAPPING;
                      return !id
                        ? ''
                        : generatePath(PATH, {
                            [PARAMS.ID]: id,
                          });
                    },
                  },
                ],
                component: Outlet,
                path: ROUTES.RECONCILIATION.SUB_PATH.VIEW.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_RECONCILIATION_FOLDER],
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.RECONCILIATION.SUB_PATH.VIEW;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
            ],
            component: Outlet,
            path: ROUTES.RECONCILIATION.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_RECONCILIATION_FOLDER],
            url: ROUTES.RECONCILIATION.PATH,
          },
          {
            children: SETTLEMENT_CHILDREN,
            component: SettlementLayout,
            intlKey: 'navigationBar.settlement',
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.Settlement />,
            path: ROUTES.SETTLEMENT.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_SETTLEMENT_CONTRACTS],
            permissionType: PERMISSION_TYPE.OR,
            testId: 'navbarSettlement',
          },
          {
            breadcrumb: 'userManagement.title',
            children: [
              {
                component: UserManagement,
                path: ROUTES.USER_MANAGEMENT.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_USERS, PERMISSIONS.VIEW_ROLES],
                permissionType: PERMISSION_TYPE.OR,
              },
              {
                breadcrumb: (params: IRouteParams): string => {
                  if (!params?.id) return '';
                  const user = queryClient.getQueryData<IUser>(
                    normalizeQueryKey([USERS_KEYS.USER, params.id]),
                  );
                  return user ? user.login : 'userManagement.users.user';
                },
                children: [
                  {
                    component: User,
                    path: ROUTES.USER_MANAGEMENT.SUB_PATH.USER.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_USERS],
                  },
                  {
                    component: UserEdit,
                    path: ROUTES.USER_MANAGEMENT.SUB_PATH.USER.SUB_PATH.EDIT.PATH,
                    permissions: [PERMISSIONS.CAN_UPDATE_USER],
                  },
                  {
                    breadcrumb: 'auditLogs.title',
                    component: AuditLogs,
                    path: ROUTES.USER_MANAGEMENT.SUB_PATH.USER.SUB_PATH.AUDIT_LOGS.PATH,
                    permissions: [
                      PERMISSIONS.CAN_VIEW_USERS,
                      PERMISSIONS.VIEW_AUDIT_LOGS,
                    ],
                    url: ROUTES.USER_MANAGEMENT.SUB_PATH.USER.SUB_PATH.AUDIT_LOGS.PATH,
                  },
                ],
                component: Outlet,
                path: ROUTES.USER_MANAGEMENT.SUB_PATH.USER.PATH,
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.USER_MANAGEMENT.SUB_PATH.USER;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
              {
                breadcrumb: 'userManagement.users.create.title',
                component: UserCreate,
                path: ROUTES.USER_MANAGEMENT.SUB_PATH.CREATE_USER,
                permissions: [PERMISSIONS.CAN_CREATE_USER],
                url: ROUTES.USER_MANAGEMENT.SUB_PATH.CREATE_USER,
              },
              {
                breadcrumb: 'rolesAndPermissions.role.create.title',
                component: CreateRole,
                path: ROUTES.USER_MANAGEMENT.SUB_PATH.CREATE_ROLE,
                permissions: [PERMISSIONS.CREATE_ROLE],
                url: ROUTES.USER_MANAGEMENT.SUB_PATH.CREATE_ROLE,
              },
              {
                breadcrumb: 'rolesAndPermissions.role.edit.title',
                component: EditRole,
                path: ROUTES.USER_MANAGEMENT.SUB_PATH.EDIT_ROLE.PATH,
                permissions: [PERMISSIONS.UPDATE_ROLE],
                url: ROUTES.USER_MANAGEMENT.SUB_PATH.EDIT_ROLE.PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.USER_MANAGEMENT.SUB_PATH.AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.VIEW_ROLES,
                  PERMISSIONS.VIEW_PERMISSIONS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.USER_MANAGEMENT.SUB_PATH.AUDIT_LOGS.PATH,
              },
            ],
            component: Outlet,
            navBarIcon: <ICONS_MAP.Users />,
            path: ROUTES.USER_MANAGEMENT.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_USERS, PERMISSIONS.VIEW_ROLES],
            permissionType: PERMISSION_TYPE.OR,
            url: ROUTES.USER_MANAGEMENT.PATH,
          },
          {
            children: CLIENTS_CHILDREN,
            component: ClientsLayout,
            intlKey: 'navigationBar.clients',
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.Clients />,
            path: ROUTES.CLIENTS_MANAGEMENT.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_CLIENTS,
              PERMISSIONS.CAN_VIEW_PAYMENT_INSTRUMENTS,
            ],
            permissionType: 'OR',
          },
          {
            children: PSP_MANAGEMENT_CHILDREN,
            component: PspManagementLayout,
            intlKey: 'navigationBar.pspManagement',
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.PSPManagement />,
            path: ROUTES.PSP_MANAGEMENT.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_PSPS,
              PERMISSIONS.CAN_VIEW_PSP_BANK_TERMINALS,
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS,
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_PARAMETERS,
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_LIMITS,
              PERMISSIONS.CAN_VIEW_TERMINAL_LINKS_GROUPS,
            ],
            permissionType: 'OR',
            testId: 'navbarPspManagement',
          },
          {
            children: TRAFFIC_MANAGEMENT_CHILDREN,
            component: TrafficManagementLayout,
            intlKey: 'navigationBar.trafficManagement',
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.TrafficManagement />,
            path: ROUTES.TRAFFIC_MANAGEMENT.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_COUNTRY_GROUPS,
              PERMISSIONS.CAN_VIEW_BIN_GROUPS,
              PERMISSIONS.CAN_VIEW_RULESETS,
              PERMISSIONS.CAN_VIEW_MERCHANT_GROUPS,
              PERMISSIONS.CAN_VIEW_BLACKLISTS_CARD_MASKS,
              PERMISSIONS.CAN_VIEW_BLACKLISTS_EMAILS,
              PERMISSIONS.CAN_VIEW_METADATA_TEMPLATES,
            ],
            permissionType: 'OR',
            testId: 'navbarTrafficManagement',
          },
          {
            breadcrumb: 'organizations.title',
            children: [
              {
                breadcrumb: 'organizations.title',
                component: Organizations,
                navBarIcon: <ICONS_MAP.OrganizationsMenu />,
                path: ROUTES.ORGANIZATIONS.PATH,
                permissions: [PERMISSIONS.VIEW_ORGANIZATIONS],
                testId: 'navbarOrganizations',
              },
              {
                breadcrumb: (params: IRouteParams) => {
                  if (!params?.id) return 'organizations.organization.title';
                  return params?.id;
                },
                children: [
                  {
                    component: ViewOrganization,
                    path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.PATH,
                    permissions: [
                      PERMISSIONS.VIEW_ORGANIZATIONS,
                      PERMISSIONS.VIEW_LEGAL_ENTITIES,
                    ],
                    permissionType: 'OR',
                  },
                  {
                    breadcrumb: (params) => {
                      return params?.targetId || '';
                    },
                    children: [
                      {
                        breadcrumb: 'auditLogs.title',
                        component: AuditLogs,
                        path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                          .AUDIT_LOGS.PATH,
                        permissions: [
                          PERMISSIONS.VIEW_ORGANIZATIONS,
                          PERMISSIONS.VIEW_AUDIT_LOGS,
                        ],
                        url: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                          .AUDIT_LOGS.PATH,
                      },
                      {
                        breadcrumb: 'auditLogs.title',
                        component: AuditLogs,
                        path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                          .AUDIT_LOGS.SUB_PATH.CHILD,
                        permissions: [
                          PERMISSIONS.VIEW_ORGANIZATIONS,
                          PERMISSIONS.VIEW_AUDIT_LOGS,
                        ],
                        url: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                          .AUDIT_LOGS.SUB_PATH.CHILD,
                      },
                    ],
                    component: Outlet,
                    path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH.AUDIT_LOGS
                      .PATH,
                    permissions: [
                      PERMISSIONS.VIEW_ORGANIZATIONS,
                      PERMISSIONS.VIEW_AUDIT_LOGS,
                    ],
                    url: (params: IRouteParams) => {
                      const id = params?.id;
                      const { PARAMS, PATH } = ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION;
                      return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                    },
                  },
                  {
                    breadcrumb: 'organizations.legalEntity.create',
                    component: CreateLegalEntity,
                    path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                      .LEGAL_ENTITY_CREATE.PATH,
                    permissions: [PERMISSIONS.CREATE_LEGAL_ENTITY],
                    url: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                      .LEGAL_ENTITY_CREATE.PATH,
                  },
                ],
                component: Outlet,
                path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.PATH,
                permissions: [
                  PERMISSIONS.VIEW_ORGANIZATIONS,
                  PERMISSIONS.VIEW_LEGAL_ENTITIES,
                ],
                permissionType: 'OR',
                url: (params: IRouteParams) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
              {
                breadcrumb: 'organizations.legalEntity.edit',
                component: EditLegalEntity,
                path: ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH
                  .LEGAL_ENTITY_EDIT.PATH,
                permissions: [
                  PERMISSIONS.VIEW_LEGAL_ENTITIES,
                  PERMISSIONS.UPDATE_LEGAL_ENTITY,
                ],
                url: (params: IRouteParams) => {
                  const id = params?.id;
                  const { PARAMS, PATH } =
                    ROUTES.ORGANIZATIONS.SUB_PATH.ORGANIZATION.SUB_PATH.LEGAL_ENTITY_EDIT;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
            ],
            component: Outlet,
            path: ROUTES.ORGANIZATIONS.PATH,
            url: ROUTES.ORGANIZATIONS.PATH,
          },
          {
            breadcrumb: 'merchants.title',
            children: [
              {
                breadcrumb: 'merchants.title',
                component: Merchants,
                navBarIcon: <ICONS_MAP.Merchants />,
                path: ROUTES.MERCHANTS.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_MERCHANTS],
                testId: 'navbarMerchants',
              },
              {
                breadcrumb: 'merchant.create',
                component: CreateMerchant,
                path: ROUTES.MERCHANTS.SUB_PATH.CREATE,
                permissions: [PERMISSIONS.CAN_CREATE_MERCHANT],
                url: ROUTES.MERCHANTS.SUB_PATH.CREATE,
              },
              {
                component: EditMerchant,
                path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.EDIT.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_MERCHANT],
              },
              {
                breadcrumb: (params: IRouteParams) => params?.id || 'merchant.title',
                children: [
                  {
                    component: Merchant,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.PATH,
                    permissions: VIEW_MERCHANT_PAGE.PERMISSIONS,
                    permissionType: VIEW_MERCHANT_PAGE.CONDITION_TYPE,
                  },
                  {
                    breadcrumb: (params) => {
                      return params?.targetId || '';
                    },
                    children: [
                      {
                        breadcrumb: 'auditLogs.title',
                        component: AuditLogs,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.AUDIT_LOGS.PATH,
                        permissions: [
                          PERMISSIONS.CAN_VIEW_MERCHANTS,
                          PERMISSIONS.VIEW_AUDIT_LOGS,
                        ],
                        url: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.AUDIT_LOGS.PATH,
                      },
                      {
                        breadcrumb: 'auditLogs.title',
                        component: AuditLogs,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.AUDIT_LOGS
                          .SUB_PATH.CHILD,
                        permissions: [
                          PERMISSIONS.CAN_VIEW_MERCHANTS,
                          PERMISSIONS.VIEW_AUDIT_LOGS,
                        ],
                        url: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.AUDIT_LOGS
                          .SUB_PATH.CHILD,
                      },
                    ],
                    component: Outlet,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.AUDIT_LOGS.PATH,
                    permissions: [
                      PERMISSIONS.CAN_VIEW_MERCHANTS,
                      PERMISSIONS.VIEW_AUDIT_LOGS,
                    ],
                    url: (params: IRouteParams) => {
                      const id = params?.id;
                      if (!id) {
                        return '';
                      }
                      const { PARAMS, PATH } = ROUTES.MERCHANTS.SUB_PATH.MERCHANT;
                      const path = generatePath(PATH, { [PARAMS.ID]: id });

                      return params.targetType
                        ? `${path}?tab=${params.targetType}`
                        : path;
                    },
                  },
                  {
                    component: CreateLimit,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.CASHIER_LIMITS.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_LIMIT],
                  },
                  {
                    component: CreateRoutingRuleset,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.ROUTING_CASCADING
                      .PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_RULESET],
                  },
                  {
                    component: CreateChangeRule,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH
                      .CHANGE_PAYMENT_METHOD.CREATE.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CHANGE_RULE],
                  },
                  {
                    component: EditChangeRule,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH
                      .CHANGE_PAYMENT_METHOD.EDIT.PATH,
                    permissions: [PERMISSIONS.CAN_UPDATE_CHANGE_RULE],
                  },
                  {
                    component: CreateCashierSettings,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.CASHIER_SETTINGS
                      .PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CASHIER_SETTING],
                  },
                  {
                    component: CreateCashierPaymentMethodGroup,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH
                      .CASHIER_PAYMENT_METHOD_GROUPS.CREATE.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_METHOD],
                  },
                  {
                    component: EditCashierPaymentMethodGroup,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH
                      .CASHIER_PAYMENT_METHOD_GROUPS.EDIT.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_METHOD],
                  },
                  {
                    component: CreateCashierPaymentMethod,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH
                      .CASHIER_PAYMENT_METHODS.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_CASHIER_PAYMENT_METHOD],
                  },
                  {
                    breadcrumb: 'merchant.paymentLinks.form.paymentLink',
                    component: CreatePaymentLink,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.PAYMENT_LINK.CREATE
                      .PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_PAYMENT_LINKS],
                    url: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.PAYMENT_LINK.CREATE
                      .PATH,
                  },
                  {
                    breadcrumb: (params) => {
                      return params?.linkId || 'merchant.paymentLinks.form.paymentLink';
                    },
                    component: EditPaymentLink,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.PAYMENT_LINK.EDIT
                      .PATH,
                    permissions: [PERMISSIONS.CAN_UPDATE_PAYMENT_LINKS],
                    url: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.PAYMENT_LINK.EDIT
                      .PATH,
                  },
                  {
                    breadcrumb: 'stylization.title',
                    children: [
                      {
                        component: Stylization,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                          .PATH,
                        permissions: [
                          PERMISSIONS.CAN_VIEW_PAYMENT_PAGE_STYLIZATIONS,
                          PERMISSIONS.CAN_VIEW_HF_STYLIZATIONS,
                        ],
                        permissionType: PERMISSION_TYPE.OR,
                      },
                      {
                        component: ThemeCreate,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                          .SUB_PATH.THEME.SUB_PATH.CREATE.PATH,
                        permissions: [PERMISSIONS.CAN_CREATE_PAYMENT_PAGE_STYLIZATION],
                      },
                      {
                        component: ThemeEdit,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                          .SUB_PATH.THEME.SUB_PATH.EDIT.PATH,
                        permissions: [PERMISSIONS.CAN_CHANGE_PAYMENT_PAGE_STYLIZATION],
                      },
                      {
                        component: HFThemeCreate,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                          .SUB_PATH.HOSTED_FIELDS_THEME.SUB_PATH.CREATE.PATH,
                        permissions: [PERMISSIONS.CAN_CREATE_HF_STYLIZATION],
                      },
                      {
                        component: HFThemeEdit,
                        path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                          .SUB_PATH.HOSTED_FIELDS_THEME.SUB_PATH.EDIT.PATH,
                        permissions: [PERMISSIONS.CAN_CHANGE_HF_STYLIZATION],
                      },
                    ],
                    component: Outlet,
                    path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION.PATH,
                    url: (params: IRouteParams) => {
                      const id = params?.id;
                      const { PATH } =
                        ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION;
                      return !id
                        ? ''
                        : generatePath(PATH, {
                            [ROUTES.MERCHANTS.SUB_PATH.MERCHANT.SUB_PATH.STYLIZATION
                              .PARAMS.ID]: id,
                          });
                    },
                  },
                ],
                component: Outlet,
                path: ROUTES.MERCHANTS.SUB_PATH.MERCHANT.PATH,
                url: (params: IRouteParams) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.MERCHANTS.SUB_PATH.MERCHANT;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
            ],
            component: Outlet,
            path: ROUTES.MERCHANTS.PATH,
            url: ROUTES.MERCHANTS.PATH,
          },
          {
            breadcrumb: 'navigationBar.merchantsSettings',
            children: MERCHANT_SETTINGS_CHILDREN,
            component: MerchantsSettingsLayout,
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.MerchantsSettingsMenu />,
            path: ROUTES.MERCHANTS_SETTINGS.PATH,
            permissions: [
              PERMISSIONS.CAN_VIEW_CASHIER_SETTINGS,
              PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_LIMITS,
              PERMISSIONS.CAN_VIEW_CASHIER_PAYMENT_METHODS,
            ],
            permissionType: 'OR',
            testId: 'navbarMerchantsSettings',
          },
          {
            breadcrumb: 'invoices.title',
            children: [
              {
                component: Invoices,
                path: ROUTES.INVOICES.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_BILLING_CONTRACTS],
              },
              {
                component: CreateBillingContract,
                path: ROUTES.INVOICES.SUB_PATH.CREATE,
                permissions: [PERMISSIONS.CAN_CREATE_BILLING_CONTRACT],
              },
              {
                component: EditBillingContract,
                path: ROUTES.INVOICES.SUB_PATH.EDIT.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_BILLING_CONTRACT],
              },
              {
                component: EditGatewayInvoice,
                path: ROUTES.INVOICES.SUB_PATH.GATEWAY_INVOICE.EDIT.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_GATEWAY_INVOICE],
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.INVOICES.SUB_PATH.AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.CAN_VIEW_BILLING_CONTRACTS,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.INVOICES.SUB_PATH.AUDIT_LOGS.PATH,
              },
              {
                breadcrumb: 'auditLogs.title',
                component: AuditLogs,
                path: ROUTES.INVOICES.SUB_PATH.GATEWAY_INVOICE.AUDIT_LOGS.PATH,
                permissions: [
                  PERMISSIONS.CAN_VIEW_GATEWAY_INVOICES,
                  PERMISSIONS.VIEW_AUDIT_LOGS,
                ],
                url: ROUTES.INVOICES.SUB_PATH.GATEWAY_INVOICE.AUDIT_LOGS.PATH,
              },
            ],
            component: Outlet,
            navBarIcon: <ICONS_MAP.Invoices />,
            path: ROUTES.INVOICES.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_BILLING_CONTRACTS],
            testId: 'navbarInvoices',
            url: ROUTES.INVOICES.PATH,
          },
          {
            breadcrumb: 'navigationBar.integrations',
            children: INTEGRATIONS_CHILDREN,
            component: IntegrationsLayout,
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.IntegrationBuilder />,
            path: ROUTES.INTEGRATIONS.PATH,
            permissions: [
              PERMISSIONS.VIEW_ADAPTERS_CODES,
              PERMISSIONS.CAN_VIEW_RESULT_CODES,
            ],
            permissionType: PERMISSION_TYPE.OR,
            testId: 'navbarIntegrations',
          },
          {
            component: LegacyIntegrationBuilderRedirect,
            path: `${LEGACY_INTEGRATION_BUILDER_PATH}/*`,
          },
          {
            component: LegacyResultCodesRedirect,
            path: LEGACY_RESULT_CODES_PATH,
          },
          {
            breadcrumb: 'navigationBar.settings',
            children: SETTINGS_CHILDREN,
            component: SettingsLayout,
            isParentFolder: true,
            navBarIcon: <ICONS_MAP.SettingMenu />,
            path: ROUTES.SETTINGS.PATH,
            permissions: [
              PERMISSIONS.CAN_CHANGE_MP_STYLIZATION,
              PERMISSIONS.CAN_VIEW_OUTPUT_CHANNELS,
              PERMISSIONS.CAN_VIEW_NOTIFICATIONS,
            ],
            permissionType: PERMISSION_TYPE.OR,
            testId: 'navbarSettings',
          },
          {
            breadcrumb: 'messageCenter.title',
            children: [
              {
                component: MessageCenter,
                path: ROUTES.MESSAGE_CENTER.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_MESSAGE_CENTER],
              },
            ],
            component: Outlet,
            navBarIcon: <ICONS_MAP.MessageCenter />,
            path: ROUTES.MESSAGE_CENTER.PATH,
            permissions: [PERMISSIONS.CAN_VIEW_MESSAGE_CENTER],
            testId: 'navbarMessageCenter',
            url: ROUTES.MESSAGE_CENTER.PATH,
          },
          {
            breadcrumb: 'dashboards.title',
            children: [
              {
                breadcrumb: 'dashboards.title',
                component: Dashboards,
                navBarIcon: <ICONS_MAP.Dashboards />,
                path: ROUTES.DASHBOARDS.PATH,
                permissions: [PERMISSIONS.CAN_VIEW_DASHBOARD_FOLDERS],
                testId: 'navbarDashboards',
              },
              {
                children: [
                  {
                    breadcrumb: 'dashboards.dashboard.title',
                    component: Dashboard,
                    path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.SUB_PATH.DASHBOARD.VIEW.PATH,
                    permissions: [PERMISSIONS.CAN_VIEW_DASHBOARDS],
                    url: (params) => {
                      const id = params?.id;
                      const { PARAMS, PATH } = ROUTES.DASHBOARDS.SUB_PATH.VIEW;
                      return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                    },
                  },
                  {
                    component: CreateDashboard,
                    path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.SUB_PATH.DASHBOARD
                      .CREATE_DASHBOARD.PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_DASHBOARD],
                  },
                  {
                    component: EditDashboard,
                    path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.SUB_PATH.DASHBOARD
                      .EDIT_DASHBOARD.PATH,
                    permissions: [PERMISSIONS.CAN_UPDATE_DASHBOARD],
                  },
                  {
                    component: CreateGraph,
                    path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.SUB_PATH.GRAPH.CREATE_GRAPH
                      .PATH,
                    permissions: [PERMISSIONS.CAN_CREATE_GRAPH],
                  },
                  {
                    component: EditGraph,
                    path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.SUB_PATH.GRAPH.EDIT_GRAPH.PATH,
                    permissions: [PERMISSIONS.CAN_UPDATE_GRAPH],
                  },
                ],
                component: Outlet,
                path: ROUTES.DASHBOARDS.SUB_PATH.VIEW.PATH,
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.DASHBOARDS.SUB_PATH.VIEW;

                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
              {
                component: CopyDashboardFolder,
                path: ROUTES.DASHBOARDS.SUB_PATH.COPY_DASHBOARD_FOLDER.PATH,
                permissions: [PERMISSIONS.CAN_COPY_DASHBOARD_FOLDER],
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } =
                    ROUTES.DASHBOARDS.SUB_PATH.COPY_DASHBOARD_FOLDER;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
              {
                component: CreateDashboardFolder,
                path: ROUTES.DASHBOARDS.SUB_PATH.CREATE_DASHBOARD_FOLDER,
                permissions: [PERMISSIONS.CAN_CREATE_DASHBOARD_FOLDER],
              },
              {
                component: EditDashboardFolder,
                path: ROUTES.DASHBOARDS.SUB_PATH.EDIT_DASHBOARD_FOLDER.PATH,
                permissions: [PERMISSIONS.CAN_UPDATE_DASHBOARD_FOLDER],
                url: (params) => {
                  const id = params?.id;
                  const { PARAMS, PATH } =
                    ROUTES.DASHBOARDS.SUB_PATH.EDIT_DASHBOARD_FOLDER;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
            ],
            component: Outlet,
            path: ROUTES.DASHBOARDS.PATH,
            url: ROUTES.DASHBOARDS.PATH,
          },
          {
            breadcrumb: 'navigationBar.userManual',
            children: [
              {
                component: UserManual,
                navBarIcon: <ICONS_MAP.Merchants />,
                path: ROUTES.USER_MANUAL.PATH,
              },
              {
                breadcrumb: (params: IRouteParams) => {
                  if (!params?.id) {
                    return '';
                  }
                  const manual = queryClient.getQueryData<IUserManual>([
                    CMS_KEYS.USER_MANUAL,
                  ]);
                  const section = manual?.sections?.find(
                    (section) => section.id.toString() === params.id,
                  );
                  return section?.title || params.id;
                },
                component: ManualSection,
                path: ROUTES.USER_MANUAL.SUB_PATH.PATH,
                url: (params: IRouteParams) => {
                  const id = params?.id;
                  const { PARAMS, PATH } = ROUTES.USER_MANUAL.SUB_PATH;
                  return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
                },
              },
            ],
            component: Outlet,
            navBarIcon: <ICONS_MAP.QuestionMark />,
            path: ROUTES.USER_MANUAL.PATH,
            testId: 'navbarUserManual',
            url: ROUTES.USER_MANUAL.PATH,
          },
        ],
        component: Layout,
        path: ROUTES.HOME.PATH,
      },
      {
        component: NotFound,
        isPrivate: true,
        path: ROUTES.NOT_FOUND.PATH,
      },
      {
        component: PermissionDenied,
        isPrivate: true,
        path: ROUTES.PERMISSION_DENIED.PATH,
      },
      {
        component: SomethingWrong,
        isPrivate: true,
        path: ROUTES.SOMETHING_WRONG.PATH,
      },
      {
        component: <Navigate replace to={ROUTES.NOT_FOUND.PATH} />,
        path: '*',
      },
    ],
    component: Outlet,
    isPrivate: true,
    path: ROUTES.HOME.PATH,
  },
] as IRoutesConfigItem[];
